import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Outlet, useLocation } from "react-router-dom";
import { Worker } from "@react-pdf-viewer/core";
import packageJson from "../../package.json";
import { getPastQueries } from "../firebase/utils";

import { Box, CssBaseline, Paper as MuiPaper } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { spacing } from "@mui/system";
import {
  setFetchHistoricalQueryState,
  setLandingDialogState,
  setQueryDrawerState,
} from "../redux/slices/main";
import { RootState } from "../redux/store";
import {
  setClusters,
  setQueries,
  setQueryResult,
  setSearchQuery,
} from "../redux/slices/search";

import useAppDispatch from "../hooks/useAppDispatch";
import useAppSelector from "../hooks/useAppSelector";

import GlobalStyle from "../components/GlobalStyle";
import Navbar from "../components/navbar/Navbar";
import dashboardItems from "../components/sidebar/dashboardItems";
import Sidebar from "../components/sidebar/Sidebar";
import SidebarResults from "../components/pages/files/SidebarResults";
import Feedback from "../components/pages/search/FeedbackDialog";
import LandingDialog from "../components/pages/search/LandingDialog";
import ConfigurationDialog from "../components/pages/search/ConfigurationDialog";
import BookmarkDialog from "../components/pages/bookmark/BookmarkDialog";
import { Query } from "../types/app";
import AlertSnackbar from "../components/AlertSnackbar";

const drawerWidth = 258;
const queryDrawerWidth = 380;

const Root = styled.div`
  display: flex;
  height: 92%;
`;

const Drawer = styled.div`
  ${(props) => props.theme.breakpoints.up("md")} {
    width: ${drawerWidth}px;
    flex-shrink: 0;
  }
`;

const QueryDrawer = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100%;
  width: ${queryDrawerWidth}px;
  padding-bottom: 80px;
  background: ${(props) => props.theme.sidebar.color};
  right: ${(props) => (props.open ? "0" : "-100%")};
  transition: right 0.5s ease-in-out;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const AppContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  max-width: 800vw;
`;

const Paper = styled(MuiPaper)(spacing);

const MainContentWrapper = styled.div`
  display: flex;
  height: 100%;
`;

const MainContent = styled(Paper)`
  flex: 1;
  background: ${(props) => props.theme.palette.background.default};

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    flex: none;
  }

  .MuiPaper-root .MuiPaper-root {
    box-shadow: none;
  }
`;

interface DashboardType {
  children?: React.ReactNode;
}

const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const router = useLocation();
  const dispatch = useAppDispatch();
  const state = useAppSelector((state: RootState) => state.main);
  const searchState = useAppSelector((state: RootState) => state.search);
  const queryDrawerOpen = state.queryDrawerOpen;
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  useEffect(() => {
    setMobileOpen(false);
    dispatch(setQueryDrawerState(false));
  }, [router.pathname, dispatch]);

  useEffect(() => {
    dispatch(setFetchHistoricalQueryState(true));
    getPastQueries().then((result) => {
      dispatch(setFetchHistoricalQueryState(false));
      if (result !== undefined) {
        const latestQuery = result[0];
        dispatch(setSearchQuery(latestQuery.query));
        dispatch(setQueryResult(latestQuery.queryResults));
        dispatch(setClusters(latestQuery.clusters));
        dispatch(setQueries(result as Query[]));
      } else {
        dispatch(setLandingDialogState(true));
      }
    });
  }, [searchState.queryLoading]);

  const theme = useTheme();
  const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
  const pdfjsVersion = packageJson.dependencies["pdfjs-dist"];

  return (
    <Root>
      <CssBaseline />
      <GlobalStyle />
      <Worker
        workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.min.js`}
      />
      <Drawer>
        <Box sx={{ display: { xs: "block", lg: "none" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            items={dashboardItems}
          />
        </Box>
        <Box sx={{ display: { xs: "none", md: "block" } }}>
          <Sidebar
            PaperProps={{ style: { width: drawerWidth } }}
            items={dashboardItems}
          />
        </Box>
      </Drawer>
      <AppContent>
        <Navbar onDrawerToggle={handleDrawerToggle} />
        <MainContentWrapper>
          <MainContent p={isLgUp ? 12 : 5}>
            {children}
            <Outlet />
          </MainContent>
          <QueryDrawer open={queryDrawerOpen}>
            <SidebarResults />
          </QueryDrawer>
        </MainContentWrapper>
        <Feedback />
      </AppContent>
      <ConfigurationDialog />
      <LandingDialog />
      <BookmarkDialog />
      <AlertSnackbar />
    </Root>
  );
};

export default Dashboard;
